import {getNormalisedYAxisLabelsFromChartSeries, AreaGradient} from "~/components/charts/chartSeriesHelpers";
import type {ChartData} from "@mantine/charts";
import colorConvert from "color-convert";
import {Area, Bar, CartesianGrid, ComposedChart, Label, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import type {LineProps} from "recharts";
import type {ComboChartSeries} from "~/components/reports/widgetSpecific/types";
import {ComposedChartType} from "~/components/reports/widgetSpecific/types";
import {graphsXAxisTickFormatter} from "~/components/reports/utilities";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {numberToHumanFriendlyStringWithNumberSystem} from "~/utilities/utilities";

export const comboChartColors = ["#EF7F77", "#4D92E4", "#DEDA8F", "#FCF471", "#A1D97D"];

export function ComboChart({
    chartData,
    chartSeries,
    xAxisKey,
    xAxisTitle,
    scalingFactor,
    height,
    maxYValue,
    maxYValueSecondary,
}: {
    chartData: ChartData;
    chartSeries: Array<ComboChartSeries>;
    xAxisKey: string;
    xAxisTitle: string;
    scalingFactor: number;
    height: number;
    maxYValue: number;
    maxYValueSecondary: number | null;
}) {
    const sortOrder = {
        [ComposedChartType.bar]: 1,
        [ComposedChartType.area]: 2,
        [ComposedChartType.line]: 3,
    };

    const yAxisLabels = getNormalisedYAxisLabelsFromChartSeries(chartSeries);
    const {theme} = useAnankePreferences();

    const sortedSeries = structuredClone(chartSeries);
    sortedSeries.sort((a, b) => sortOrder[a.chartType] - sortOrder[b.chartType]);

    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            <ComposedChart
                data={chartData}
                margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                }}
                barGap={"6%"}
                maxBarSize={32}
            >
                <XAxis
                    dataKey={xAxisKey}
                    scale="auto"
                    tickFormatter={(value) => graphsXAxisTickFormatter(value)}
                    tickMargin={20}
                    tickSize={0}
                    tick={{
                        fontSize: "10px",
                    }}
                    axisLine={false}
                >
                    <Label
                        value={xAxisTitle}
                        dy={28}
                        style={{
                            textAnchor: "middle",
                            fontSize: "10px",
                        }}
                    />
                </XAxis>

                <CartesianGrid
                    horizontal={true}
                    vertical={false}
                    stroke={theme === "dark" ? "#ADACAC" : "#262626"}
                    strokeDasharray="7 5"
                />

                {yAxisLabels.left !== null && (
                    <YAxis
                        yAxisId={"left"}
                        orientation={"left"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={[0, maxYValue]}
                    >
                        <Label
                            value={yAxisLabels.left.join(", ")}
                            position={"insideLeft"}
                            offset={0}
                            angle={-90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                {yAxisLabels.right !== null && (
                    <YAxis
                        yAxisId={"right"}
                        orientation={"right"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={maxYValueSecondary === null ? undefined : [0, maxYValueSecondary]}
                    >
                        <Label
                            value={yAxisLabels.right.join(", ")}
                            position={"insideRight"}
                            offset={0}
                            angle={90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                <Tooltip
                    contentStyle={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                        background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                        border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                    labelFormatter={(value) => {
                        return graphsXAxisTickFormatter(value);
                    }}
                    formatter={(value) => {
                        const number = parseFloat(value.toString());
                        return Number.isInteger(number) === true ? number : number.toFixed(2);
                    }}
                />

                <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                        top: "-4px",
                        fontSize: "10px",
                    }}
                />

                <defs>
                    {sortedSeries.map((series, seriesIndex) => {
                        if (series.chartType === ComposedChartType.area) {
                            return (
                                <AreaGradient
                                    // color={series.color}
                                    color={comboChartColors[seriesIndex % comboChartColors.length]}
                                    fillOpacity={0.7}
                                    // id={series.color.replaceAll("#", "")}
                                    id={comboChartColors[seriesIndex % comboChartColors.length].replaceAll("#", "")}
                                    withGradient={true}
                                    key={seriesIndex}
                                />
                            );
                        }
                    })}
                </defs>

                {sortedSeries.map((series, seriesIndex) => {
                    if (series.chartType === ComposedChartType.bar) {
                        return (
                            <Bar
                                dataKey={series.name}
                                // barSize={referenceBarWidth * scalingFactor}
                                fill={comboChartColors[seriesIndex % comboChartColors.length]}
                                // fill={series.color}
                                fillOpacity={0.8}
                                key={seriesIndex}
                                yAxisId={series.yAxisId}
                                radius={2}
                            />
                        );
                    }
                    if (series.chartType === ComposedChartType.line) {
                        const hsl = colorConvert.hex.hsl(comboChartColors[seriesIndex % comboChartColors.length]!);

                        hsl[2] += 10;
                        hsl[1] -= 5;

                        const hex = colorConvert.hsl.hex(hsl);

                        const isBarPresent = chartSeries.find((currentSeries) => currentSeries.chartType === ComposedChartType.bar);
                        let dot: LineProps["dot"] = undefined;
                        if (isBarPresent === undefined) {
                            dot = false;
                        } else {
                            dot = {
                                r: 4,
                                strokeWidth: 2,
                                height: "12px",
                                width: "12px",
                                fill: `#${hex}`,
                                stroke: `#${hex}`,
                                className: "line-chart-dot-pop-in",
                            };
                        }

                        return (
                            <Line
                                dataKey={series.name}
                                stroke={comboChartColors[seriesIndex % comboChartColors.length]}
                                name={series.label}
                                key={seriesIndex}
                                yAxisId={series.yAxisId}
                                type="bump"
                                dot={dot}
                                strokeWidth={3}
                                activeDot={{
                                    radius: 1,
                                    strokeWidth: 0,
                                }}
                                legendType="plainline"
                            />
                        );
                    }
                    if (series.chartType === ComposedChartType.area) {
                        return (
                            <Area
                                dataKey={series.name}
                                // fill={`url(#${series.color.replaceAll(
                                //     "#",
                                //     "",
                                // )})`}
                                fill={`url(#${comboChartColors[seriesIndex % comboChartColors.length].replaceAll("#", "")})`}
                                // stroke={series.color}
                                stroke={comboChartColors[seriesIndex % comboChartColors.length]}
                                key={seriesIndex}
                                yAxisId={series.yAxisId}
                                type="bump"
                                dot={false}
                                strokeWidth={3}
                                activeDot={{
                                    radius: 1,
                                    strokeWidth: 0,
                                }}
                                legendType="plainline"
                            />
                        );
                    }
                })}
            </ComposedChart>
        </ResponsiveContainer>
    );
}
